import { Edges, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { buildBelt, buildCentrifugal, buildDuct, buildDuctPlateWithHole, buildESPConeGeometry, buildHollowFrame } from "../Utils/geometryUtils";
import { useEffect, useMemo, useRef, useState } from "react";
import { align, fromMM } from "../Utils/frameUtils";
import { COMPONENT_ALIGNMENT, COMPONENT_SIDE } from "../Constants";
import Flange from "./Flange";
import { MathUtils } from "three";
import { useStore } from "../Store/zustandStore";

export default function ExternalFan({ frame, selected }) {
  const groupRef = useRef();
  const shaftRef = useRef();

  const [frameThickness] = useStore((state) => [fromMM(state.frameThickness)]);

  const frameAlignmentZ = COMPONENT_ALIGNMENT.BACK;

  const frameLength = fromMM(frame.length);
  const frameHeight = fromMM(frame.height);
  const frameWidth = fromMM(frame.width);

  const fanWidth = fromMM(frame.fan?.width || 322);
  const fanSize = fromMM(frame.fan?.size || 850);
  const fanDistanceFromTop = fromMM(frame.fan?.distanceFromTop || 50);

  const coneLength = fromMM(frame.fan?.coneLength || 500);

  // Total length of the 3-segment flexible connector (75 + 100 + 75 mm).
  const flexibleConnectorLength = fromMM(75 + 100 + 75);

  const fanOutletHeight = fromMM(frame.fan?.outletHeight || 622);
  const fanOutletLength = fromMM(frame.fan?.outletLength || 70);
  const fanRotation = MathUtils.degToRad(frame.fan?.angle || 0);
  const fanRotationY = MathUtils.degToRad(frame.fan?.rotation || -90);

  const positionOffsetX = fromMM(frame.fan?.positionOffsetX ?? 0);
  const positionOffsetZ = fromMM(frame.fan?.positionOffsetZ ?? 0);

  const fanFrameLength = fanSize - fanOutletLength;

  const fanAlignment = COMPONENT_ALIGNMENT.RIGHT; // frame.componentAlignment;
  const flangeAlignment = frame.flangeAlignment || COMPONENT_ALIGNMENT.LEFT;
  const shaftSide = fanAlignment == COMPONENT_ALIGNMENT.LEFT ? -1 : 1;

  const flangeConnectorSize = [fanOutletHeight, flangeAlignment == COMPONENT_ALIGNMENT.TOP ? fanDistanceFromTop + frameThickness : frameThickness, fanWidth];

  // Mounting

  const mountingFrameLength = fromMM(frame.fan?.mountingFrameLength || 1250);
  const mountingFrameWidth = fromMM(frame.fan?.mountingFrameWidth || 80);
  const mountingFrameHeight = fromMM(frame.fan?.mountingFrameHeight || 40);
  const isolatorHeight = fromMM(frame.fan?.isolatorHeight || 40);

  // Fan mounting frame sits on top of the floor mounting frame + isolators (not anchored to the fan above).
  //   floor mounting top = -frameHeight/2 + mountingFrameHeight
  //   isolator top       = -frameHeight/2 + mountingFrameHeight + isolatorHeight
  //   fan mounting bottom = isolator top → center at +mountingFrameHeight/2 above
  const fanFrameMountingPositionY = -frameHeight / 2 + mountingFrameHeight + isolatorHeight + mountingFrameHeight / 2;

  const motorMountingLength = fromMM(frame.fan?.motorMountingLength || 300);
  const motorMountingWidth = fromMM(frame.fan?.motorMountingWidth || 300);
  const motorMountingHeight = fromMM(frame.fan?.motorMountingHeight || 40);

  const feetRadius = fromMM(frame.fan?.feetRadius || 20);

  const feetHeight = isolatorHeight;

  // Motor

  const motorRadius = fromMM(frame.fan?.motorRadius || 80);
  const motorLength = fromMM(frame.fan?.motorLength || 300);

  const motorShaftLength = fromMM(frame.fan?.motorShaftLength || 100);

  const pulleyThickness = fromMM(frame.fan?.pulleyThickness || 30);
  const fanPulleyRadius = fromMM(frame.fan?.fanPulleyRadius || 80);
  const motorPulleyRadius = fromMM(frame.fan?.motorPulleyRadius || 60);

  const shaftRadius = fromMM(frame.fan?.shaftRadius || 90);
  //const shaftLength = fromMM(frame.fan?.shaftLength || 410);

  const shaftBaseLength = fromMM(frame.fan?.shaftBaseLength || 290);
  const shaftBaseWidth = fromMM(frame.fan?.shaftBaseWidth || 355);

  const inletHeight = fromMM(300);
  const inletWidth = fromMM(340);
  const inletLength = fromMM(10);

  // Positions

  const pulleyPosition = frame.fan?.pulleyPosition ?? -1;
  const motorOutside = frame.fan?.motorOutside ?? true;

  const fanPosition = [
    align(0, mountingFrameLength, fanSize, fanAlignment),
    // Anchor fan body to sit on top of its mounting frame (independent of AHU height).
    // mounting top = fanFrameMountingPositionY + mountingFrameHeight/2; fan center sits fanSize/2 above.
    fanFrameMountingPositionY + mountingFrameHeight / 2 + fanSize / 2,

    // Mirror of motorPosition[2] so the fan and motor sit symmetrically on their shared mounting frame.
    motorOutside ? align(0, fanWidth * 2 + mountingFrameWidth * 2, fanWidth + mountingFrameWidth, COMPONENT_ALIGNMENT.FRONT) : 0,
  ];

  const flangeConnectorPosition = [
    flangeAlignment == COMPONENT_ALIGNMENT.TOP
      ? align(fanPosition[0], fanSize, fanOutletHeight, COMPONENT_ALIGNMENT.LEFT)
      : align(fanPosition[0], fanSize, -frameThickness, COMPONENT_ALIGNMENT.RIGHT),
    flangeAlignment == COMPONENT_ALIGNMENT.TOP
      ? align(0, frameHeight + frameThickness * 2, fanDistanceFromTop + frameThickness, COMPONENT_ALIGNMENT.TOP)
      : align(fanPosition[1], fanSize, fanOutletHeight, COMPONENT_ALIGNMENT.TOP),
    fanPosition[2],
  ];

  const motorBasePosition = [
    align(0, mountingFrameLength, motorMountingLength, COMPONENT_ALIGNMENT.LEFT),
    align(fanFrameMountingPositionY, mountingFrameHeight, -motorMountingHeight, COMPONENT_ALIGNMENT.TOP) + mountingFrameHeight,
    motorOutside ? align(0, fanWidth * 2 + mountingFrameWidth * 2, fanWidth + mountingFrameWidth, COMPONENT_ALIGNMENT.BACK) : 0,
  ];

  const motorPosition = [
    motorBasePosition[0],
    align(motorBasePosition[1], motorMountingHeight, -motorRadius * 2, COMPONENT_ALIGNMENT.TOP),
    motorOutside ? align(0, fanWidth * 2 + mountingFrameWidth * 2, fanWidth + mountingFrameWidth, COMPONENT_ALIGNMENT.BACK) : 0,
  ];

  const [fanPulleyPosition, setFanPulleyPosition] = useState([
    fanPosition[0],
    fanPosition[1] + -(fanSize * 0.25) / 2,
    motorPosition[2] + (motorLength / 2 + motorShaftLength) * pulleyPosition,
  ]);
  const motorPulleyPosition = [motorPosition[0], motorPosition[1], motorPosition[2] + (motorLength / 2 + motorShaftLength) * pulleyPosition];

  // Previous version (kept for reference): centered the shaft at motorPosition[2]
  // with a fixed shaftLength, so it floated free of the fan body whenever
  // fanWidth / motor offset / pulley position changed.
  // const shaftPosition = motorOutside ? [fanPulleyPosition[0], fanPulleyPosition[1], motorPosition[2]] : null;

  // Bridge the shaft from the fan body's back face to the fan pulley.
  const fanBodyBackZ = fanPosition[2] + fanWidth / 2;
  const shaftLength = frame.fan?.shaftLength != null ? fromMM( frame.fan?.shaftLength) : Math.abs(fanPulleyPosition[2] - fanBodyBackZ);
  const shaftPosition = motorOutside ? [fanPulleyPosition[0], fanPulleyPosition[1], (fanBodyBackZ + fanPulleyPosition[2]) / 2] : null;

  const shaftBaseHeight = motorOutside ? shaftPosition[1] - fanPulleyRadius * 2 - align(0, frameHeight, mountingFrameHeight, COMPONENT_ALIGNMENT.BOTTOM) : 0;

  const shaftBasePosition = motorOutside ? [fanPulleyPosition[0], fanPulleyPosition[1] - fanPulleyRadius - shaftBaseHeight / 2, motorPosition[2] - shaftBaseWidth / 2] : null;

  //const shaftBaseFeetHeight = motorOutside ? shaftBasePosition[1]

  // Geometries

  const flangeConnectorGeometry = useMemo(() => {
    return buildHollowFrame(flangeConnectorSize[1], flangeConnectorSize[0], flangeConnectorSize[2], 0.01);
  }, [fanSize, fanOutletHeight, fanWidth, flangeAlignment]);

  const fan_geometry = useMemo(() => {
    return buildCentrifugal(fanSize, fanSize, fanWidth, fanOutletHeight, fanOutletLength, shaftSide);
  }, [fanSize, fanOutletHeight, fanWidth, shaftSide]);

  const fanShaftRef = useRef();
  useEffect(() => {
    if (fanShaftRef.current) {
      fanShaftRef.current.updateMatrixWorld();
      // overwrote x/y and applied the local matrix — but the matrix already
      // contained the translation, so z was effectively added twice. It only
      // worked because index [2] of the resulting array was recomputed below.
      // const matrix = fanShaftRef.current.matrix;
      // const worldVector = fanShaftRef.current.position.clone();
      // worldVector.x = 0;
      // worldVector.y = -(fanSize * 0.25) / 2;
      // worldVector.applyMatrix4(matrix);
      const worldVector = new THREE.Vector3(0, -(fanSize * 0.25) / 2, 0).applyMatrix4(fanShaftRef.current.matrix);
      setFanPulleyPosition([worldVector.x, worldVector.y, motorPosition[2] + (motorLength / 2 + motorShaftLength) * pulleyPosition]);
    }
  }, [fanShaftRef, frameLength, fanRotation, fanAlignment, pulleyPosition, motorOutside]);

  // motorOutside ? [fanPulleyPosition[0], fanPulleyPosition[1], motorPosition[2] + 0.05] : null;
  //const inletPosition = motorOutside ? [fanPulleyPosition[0], fanPulleyPosition[1], fanPulleyPosition[2]] : [-1, -0.1, 0.6];
  const boxFrameThickness = fromMM(0);

  const NoFrame = true;
  const YOffSet = -(NoFrame ? frameThickness : 0);
  const [groupPosition, setGroupPosition] = useState([0, YOffSet, 0]);

  const calculateFanInletPosition = () => {
    // Inlet hole center in fan-group local space (matches buildCentrifugal's front face).
    let x = 0;
    let y = -fanSize / 8;
    let z = fanWidth / 2;
    // 1. Apply inner-group Z rotation (fanRotation).
    const cosZ = Math.cos(fanRotation);
    const sinZ = Math.sin(fanRotation);
    [x, y] = [x * cosZ - y * sinZ, x * sinZ + y * cosZ];
    // 2. Translate by fanPosition (still in groupRef-local space).
    x += fanPosition[0];
    y += fanPosition[1];
    z += fanPosition[2];
    // 3. Apply outer-group Y rotation (fanRotationY).
    const cosY = Math.cos(fanRotationY);
    const sinY = Math.sin(fanRotationY);
    [x, z] = [x * cosY + z * sinY, -x * sinY + z * cosY];
    // 4. Translate by groupPosition (now in world space).
    x += groupPosition[0];
    y += groupPosition[1];
    z += groupPosition[2];
    return [x, y, z];
  };

  const flexibleConnectorPosition = useMemo(() => {
    const inletPosition = calculateFanInletPosition();
    return [inletPosition[0], inletPosition[1], inletPosition[2]];
  }, [fanSize, fanWidth, fanRotation, fanRotationY, fanPosition[0], fanPosition[1], fanPosition[2], groupPosition]);

  const coneGeom = useMemo(() => {
    const extendedLength = !frame.hasFrame ? frameThickness : 0;
    const extendedHeight = !frame.hasFrame ? frameThickness : 0;

    const inletPosition = calculateFanInletPosition();

    // buildESPConeGeometry mirrors X (section[0].x *= -1) before rendering.
    // The full 3-segment flexible connector sits between cone narrow end and inlet face.
    const leftPositionX = -inletPosition[0] + flexibleConnectorLength;

    //return new THREE.BoxGeometry(1, 0.001, 1);

    return buildESPConeGeometry([
      [
        new THREE.Vector3(extendedLength + frameLength / 2, frameHeight / 2, -frameWidth / 2),
        frameWidth,
        0,
        new THREE.Vector3(
          leftPositionX,
          //   espGroupPosition[1] +
          inletPosition[1] + (inletHeight - boxFrameThickness * 2) / 2,
          inletPosition[2] - (inletWidth - boxFrameThickness * 2) / 2,
        ),
        inletWidth - boxFrameThickness * 2,
        0,
      ],
      // bottom
      [
        new THREE.Vector3(extendedLength + frameLength / 2, -frameHeight / 2, -frameWidth / 2),
        frameWidth,
        0,
        new THREE.Vector3(
          leftPositionX,
          //  espGroupPosition[1] +
          inletPosition[1] - (inletHeight - boxFrameThickness * 2) / 2,
          inletPosition[2] - (inletWidth - boxFrameThickness * 2) / 2,
        ),
        inletWidth - boxFrameThickness * 2,
        0,
      ],
      // back
      [
        new THREE.Vector3(extendedLength + frameLength / 2, frameHeight / 2, -frameWidth / 2),
        0,
        -frameHeight,
        new THREE.Vector3(
          leftPositionX,
          //   espGroupPosition[1] +
          inletPosition[1] + (inletHeight - boxFrameThickness * 2) / 2,
          inletPosition[2] - (inletWidth - boxFrameThickness * 2) / 2,
        ),
        0,
        -(inletHeight - boxFrameThickness * 2),
      ],
      // front
      [
        new THREE.Vector3(extendedLength + frameLength / 2, frameHeight / 2 , frameWidth / 2),
        0,
        -frameHeight,
        new THREE.Vector3(
          leftPositionX,
          //  espGroupPosition[1] +
          inletPosition[1] + (inletHeight - boxFrameThickness * 2) / 2,
          inletPosition[2] + (inletWidth - boxFrameThickness * 2) / 2,
        ),
        0,
        -(inletHeight - boxFrameThickness * 2),
      ],
    ]);
  }, [frameHeight, frameLength, frameWidth, inletHeight, inletWidth, inletLength, fanRotation, fanRotationY, fanSize, fanWidth, fanPosition[0], fanPosition[1], fanPosition[2], groupPosition, coneLength, flexibleConnectorLength]);

  useEffect(() => {
    if (groupRef.current) {
      const box = new THREE.Box3().setFromObject(groupRef.current);
      const size = new THREE.Vector3();
      box.getSize(size);
      let pos = new THREE.Vector3(align(0, frameLength, size.x, frame.componentAlignment), 0, align(0, frameWidth, size.z, frameAlignmentZ));

      // Shift X so the fan inlet lands at coneNarrowWorldX + flexibleConnectorLength.
      const coneNarrowWorldX = -frameLength / 2 + coneLength;
      const desiredInletWorldX = coneNarrowWorldX + flexibleConnectorLength;
      const inletAlignedX = desiredInletWorldX + fanPosition[2] + fanWidth / 2;

      // Shift Y so the FLOOR mounting frame bottom aligns with the AHU base frame bottom.
      // (Floor mounting frame is at groupRef-local Y = -frameHeight/2 + mountingFrameHeight/2,
      //  so its bottom is at -frameHeight/2 in groupRef-local.)
      const baseFrameHeight = fromMM(frame.baseFrame?.height || 0);
      const groundLevelY = -frameHeight / 2 - frameThickness - baseFrameHeight;
      const inletAlignedY = groundLevelY + frameHeight / 2;

      setGroupPosition([inletAlignedX + positionOffsetX, inletAlignedY, pos.z + positionOffsetZ]);
    }
  }, [frame.componentAlignment, fanRotationY, YOffSet, frameLength, frameHeight, frameWidth, frameThickness, frame.baseFrame?.height, mountingFrameLength, mountingFrameHeight, motorOutside, coneLength, flexibleConnectorLength, fanWidth, fanPosition[2], fanFrameMountingPositionY, positionOffsetX, positionOffsetZ]);

  return (
    <group userData={{ excludeProjections: ["left"] }}>
      {motorOutside && (
        <mesh geometry={coneGeom}>
          <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
          </Edges>
        </mesh>
      )}
      {motorOutside && (
        <mesh position={[flexibleConnectorPosition[0] - fromMM(75) / 2, flexibleConnectorPosition[1], flexibleConnectorPosition[2]]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.25, 0.25, fromMM(75), 16]} />
          <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
          </Edges>
        </mesh>
      )}
      {motorOutside && (
        <mesh position={[flexibleConnectorPosition[0] - (fromMM(75) + fromMM(100) / 2), flexibleConnectorPosition[1], flexibleConnectorPosition[2]]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.24, 0.24, fromMM(100), 16]} />
          <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
          </Edges>
        </mesh>
      )}
      {motorOutside && (
        <mesh
          position={[flexibleConnectorPosition[0] - (fromMM(75) + fromMM(100) + fromMM(75) / 2), flexibleConnectorPosition[1], flexibleConnectorPosition[2]]}
          rotation={[0, 0, Math.PI / 2]}
        >
          <cylinderGeometry args={[0.25, 0.25, fromMM(75), 16]} />
          <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
          </Edges>
        </mesh>
      )}
      <group ref={groupRef} rotation={[0, fanRotationY, 0]} position={groupPosition}>
        <group position={fanPosition} rotation={[0, 0, fanRotation]} ref={fanShaftRef}>
          {fan_geometry.map((g, i) => {
            return (
              <mesh key={i} geometry={g}>
                {/* <meshStandardMaterial color={i == fan_geometry.length - 1 ? "Black" : "SlateGrey"} side={THREE.DoubleSide} /> */}
                <meshStandardMaterial color={"SlateGrey"} side={THREE.DoubleSide} />
                <Edges scale={1} renderOrder={1000}>
                  <meshBasicMaterial transparent color="#526477" widthTest={true} />
                </Edges>
              </mesh>
            );
          })}
          <FanFrame width={fanFrameLength} height={fanSize} depth={fromMM(40)} position={[align(0, fanSize, fanFrameLength, COMPONENT_ALIGNMENT.LEFT), 0, fanWidth / 2]}></FanFrame>
          <FanFrame
            width={fanFrameLength}
            height={fanSize}
            depth={fromMM(40)}
            position={[align(0, fanSize, fanFrameLength, COMPONENT_ALIGNMENT.LEFT), 0, -fanWidth / 2]}
            rotation={[0, Math.PI, 0]}
          ></FanFrame>
          {motorOutside && (
            <mesh position={[0, -0.1, -fanWidth / 2]} rotation={[0, Math.PI / 2, Math.PI / 2]}>
              <cylinderGeometry args={[0.25, 0.25, 0.01, 16]} />
              <meshStandardMaterial color={"SlateGrey"} side={THREE.DoubleSide} />
              <Edges scale={1} renderOrder={1000}>
                <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
              </Edges>
            </mesh>
          )}
          {motorOutside && (
            <mesh position={[0, -0.1, fanWidth / 2]} rotation={[0, Math.PI / 2, Math.PI / 2]}>
              <cylinderGeometry args={[0.25, 0.25, 0.01, 16]} />
              <meshStandardMaterial color={"SlateGrey"} side={THREE.DoubleSide} />
              <Edges scale={1} renderOrder={1000}>
                <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
              </Edges>
            </mesh>
          )}
        </group>

        <mesh position={flangeConnectorPosition} geometry={flangeConnectorGeometry} rotation={[0, 0, flangeAlignment == COMPONENT_ALIGNMENT.TOP ? Math.PI / 2 : 0]}>
          {/* <boxGeometry args={flangeConnectorSize} /> */}
          <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="#526477" widthTest={true} />
          </Edges>
        </mesh>

        <MotorPulleysAndBelt
          pulleyThickness={pulleyThickness}
          fanPulleyRadius={fanPulleyRadius}
          motorPulleyRadius={motorPulleyRadius}
          fanPulleyPosition={fanPulleyPosition}
          motorPulleyPosition={motorPulleyPosition}
          shaftPosition={shaftPosition}
          shaftRadius={shaftRadius}
          shaftLength={shaftLength}
          shaftRef={shaftRef}
          hasGuard={motorOutside}
        />

        {motorOutside && <FanFrame width={shaftBaseLength} height={shaftBaseHeight} depth={shaftBaseWidth} position={shaftBasePosition} rotation={[0, 0, 0]} />}

        {/* <group position={[align(-fanOutletLength, frameLength, mountingFrameLength, fanAlignment), 0, 0]}> */}
        <group position={[0, 0, 0]}>
          {/*Fan mounting*/}
          <MountingFrame
            length={mountingFrameLength}
            height={mountingFrameHeight}
            width={mountingFrameWidth}
            position={[
              0,
              fanFrameMountingPositionY,
              align(0, fanWidth * (motorOutside ? 2 : 1), -mountingFrameWidth, motorOutside ? COMPONENT_ALIGNMENT.CENTER : COMPONENT_ALIGNMENT.BACK),
            ]}
          />
          <MountingFrame
            length={mountingFrameLength}
            height={mountingFrameHeight}
            width={mountingFrameWidth}
            position={[0, fanFrameMountingPositionY, align(0, fanWidth * (motorOutside ? 2 : 1), -mountingFrameWidth, COMPONENT_ALIGNMENT.FRONT)]}
          />
          {motorOutside && (
            <MountingFrame
              length={mountingFrameLength}
              height={mountingFrameHeight}
              width={mountingFrameWidth}
              position={[0, fanFrameMountingPositionY, align(0, fanWidth * 2, -mountingFrameWidth, COMPONENT_ALIGNMENT.BACK)]}
            />
          )}

          {/* Isolators — redundant with the existing MountingFoot block (Floor mounting and feet).
             Kept commented for reference in case the existing logic doesn't cover all cases. */}
          {/*
          {[fanPosition[0] - fanSize / 2 + fromMM(60), fanPosition[0] + fanSize / 2 - fromMM(60)].map((xPos) =>
            [fanPosition[2] - fanWidth / 2 + fromMM(40), fanPosition[2] + fanWidth / 2 - fromMM(40)].map((zPos, i) => (
              <MountingFoot
                key={`isolator-${xPos}-${i}`}
                radius={feetRadius}
                height={isolatorHeight}
                position={[xPos, fanPosition[1] - fanSize / 2 - isolatorHeight / 2, zPos]}
              />
            )),
          )}
          */}

          {/*Motor mounting*/}
          <MountingFrame
            length={fanWidth + mountingFrameWidth}
            height={mountingFrameHeight}
            width={mountingFrameWidth}
            position={[
              align(0, mountingFrameLength, mountingFrameWidth, COMPONENT_ALIGNMENT.LEFT),
              fanFrameMountingPositionY + mountingFrameHeight,
              motorOutside ? align(0, fanWidth * 2 + mountingFrameWidth * 2, fanWidth + mountingFrameWidth, COMPONENT_ALIGNMENT.BACK) : 0,
            ]}
            rotation={[0, Math.PI / 2, 0]}
          />
          <MountingFrame
            length={fanWidth + mountingFrameWidth}
            height={mountingFrameHeight}
            width={mountingFrameWidth}
            position={[
              align(0, mountingFrameLength, mountingFrameWidth, COMPONENT_ALIGNMENT.LEFT) + motorMountingLength - mountingFrameWidth,
              fanFrameMountingPositionY + mountingFrameHeight,
              motorOutside ? align(0, fanWidth * 2 + mountingFrameWidth * 2, fanWidth + mountingFrameWidth, COMPONENT_ALIGNMENT.BACK) : 0,
            ]}
            rotation={[0, Math.PI / 2, 0]}
          />
          <MotorBase position={motorBasePosition} length={motorMountingLength} height={motorMountingHeight} width={motorMountingWidth} />
          <Motor position={motorPosition} rotation={[Math.PI / 2, 0, 0]} radius={motorRadius} length={motorLength} shaftLength={motorShaftLength} shaftSide={pulleyPosition} />

          {/*Floor mounting and feet*/}
          <MountingFrame
            length={fanWidth * (motorOutside ? 2 : 1) + mountingFrameWidth * 2}
            height={mountingFrameHeight}
            width={mountingFrameWidth}
            position={[align(0, mountingFrameLength, mountingFrameWidth, COMPONENT_ALIGNMENT.LEFT), align(0, frameHeight, mountingFrameHeight, COMPONENT_ALIGNMENT.BOTTOM), 0]}
            rotation={[0, Math.PI / 2, 0]}
          />
          <MountingFrame
            length={fanWidth * (motorOutside ? 2 : 1) + mountingFrameWidth * 2}
            height={mountingFrameHeight}
            width={mountingFrameWidth}
            position={[align(0, mountingFrameLength, mountingFrameWidth, COMPONENT_ALIGNMENT.RIGHT), align(0, frameHeight, mountingFrameHeight, COMPONENT_ALIGNMENT.BOTTOM), 0]}
            rotation={[0, Math.PI / 2, 0]}
          />
          <MountingFoot
            radius={feetRadius}
            height={isolatorHeight}
            position={[
              align(0, mountingFrameLength, mountingFrameWidth, COMPONENT_ALIGNMENT.RIGHT),
              align(0, frameHeight, feetHeight, COMPONENT_ALIGNMENT.BOTTOM) + mountingFrameHeight,
              align(0, fanWidth * (motorOutside ? 2 : 1), -mountingFrameWidth, COMPONENT_ALIGNMENT.FRONT),
            ]}
          />
          <MountingFoot
            radius={feetRadius}
            height={isolatorHeight}
            position={[
              align(0, mountingFrameLength, mountingFrameWidth, COMPONENT_ALIGNMENT.RIGHT),
              align(0, frameHeight, feetHeight, COMPONENT_ALIGNMENT.BOTTOM) + mountingFrameHeight,
              align(0, fanWidth * (motorOutside ? 2 : 1), -mountingFrameWidth, motorOutside ? COMPONENT_ALIGNMENT.CENTER : COMPONENT_ALIGNMENT.BACK),
            ]}
          />
          <MountingFoot
            radius={feetRadius}
            height={isolatorHeight}
            position={[
              align(0, mountingFrameLength, mountingFrameWidth, COMPONENT_ALIGNMENT.LEFT),
              align(0, frameHeight, feetHeight, COMPONENT_ALIGNMENT.BOTTOM) + mountingFrameHeight,
              align(0, fanWidth * (motorOutside ? 2 : 1), -mountingFrameWidth, COMPONENT_ALIGNMENT.FRONT),
            ]}
          />
          <MountingFoot
            radius={feetRadius}
            height={isolatorHeight}
            position={[
              align(0, mountingFrameLength, mountingFrameWidth, COMPONENT_ALIGNMENT.LEFT),
              align(0, frameHeight, feetHeight, COMPONENT_ALIGNMENT.BOTTOM) + mountingFrameHeight,
              align(0, fanWidth * (motorOutside ? 2 : 1), -mountingFrameWidth, motorOutside ? COMPONENT_ALIGNMENT.CENTER : COMPONENT_ALIGNMENT.BACK),
            ]}
          />

          {motorOutside && (
            <MountingFoot
              radius={feetRadius}
              height={isolatorHeight}
              position={[
                align(0, mountingFrameLength, mountingFrameWidth, COMPONENT_ALIGNMENT.RIGHT),
                align(0, frameHeight, feetHeight, COMPONENT_ALIGNMENT.BOTTOM) + mountingFrameHeight,
                align(0, fanWidth * 2, -mountingFrameWidth, COMPONENT_ALIGNMENT.BACK),
              ]}
            />
          )}
          {motorOutside && (
            <MountingFoot
              radius={feetRadius}
              height={isolatorHeight}
              position={[
                align(0, mountingFrameLength, mountingFrameWidth, COMPONENT_ALIGNMENT.LEFT),
                align(0, frameHeight, feetHeight, COMPONENT_ALIGNMENT.BOTTOM) + mountingFrameHeight,
                align(0, fanWidth * 2, -mountingFrameWidth, COMPONENT_ALIGNMENT.BACK),
              ]}
            />
          )}
        </group>
      </group>

      {frame.hasFlange && (
        <Flange
          frame={frame}
          getColour={() => (selected ? "Orange" : "DarkGrey")}
          flangeWidth={fanWidth}
          flangeHeight={fanOutletHeight}
          flangeDistanceFromTop={flangeAlignment == COMPONENT_ALIGNMENT.TOP ? fanSize - fanOutletHeight : fanDistanceFromTop}
        />
      )}
    </group>
  );
}

export function MotorPulleysAndBelt({
  motorPulleyRadius,
  fanPulleyRadius,
  motorPulleyPosition,
  fanPulleyPosition,
  pulleyThickness,
  shaftPosition,
  shaftRadius,
  shaftLength,
  shaftRef,
  hasGuard,
}) {
  const beltThickness = fromMM(5);
  const beltGeometry = useMemo(() => {
    const startPoint = [fanPulleyPosition[0], fanPulleyPosition[1] + fanPulleyRadius - beltThickness, fanPulleyPosition[2]];
    const endPoint = [motorPulleyPosition[0], motorPulleyPosition[1] + motorPulleyRadius - beltThickness, motorPulleyPosition[2]];
    return buildBelt(startPoint, endPoint, beltThickness);
  }, [motorPulleyRadius, fanPulleyRadius, motorPulleyPosition, fanPulleyPosition]);

  const beltGeometryBottom = useMemo(() => {
    const startPoint = [fanPulleyPosition[0], fanPulleyPosition[1] - fanPulleyRadius + beltThickness, fanPulleyPosition[2]];
    const endPoint = [motorPulleyPosition[0], motorPulleyPosition[1] - motorPulleyRadius + beltThickness, motorPulleyPosition[2]];
    return buildBelt(startPoint, endPoint, beltThickness);
  }, [motorPulleyRadius, fanPulleyRadius, motorPulleyPosition, fanPulleyPosition]);

  const guardWidth = useMemo(() => {
    const dx = motorPulleyPosition[0] - fanPulleyPosition[0];
    const dy = motorPulleyPosition[1] - motorPulleyRadius + beltThickness - fanPulleyPosition[1] - fanPulleyRadius + beltThickness;
    return Math.sqrt(dx * dx + dy * dy) + motorPulleyRadius * 2 + fanPulleyRadius * 2;
  }, [motorPulleyRadius, fanPulleyRadius, motorPulleyPosition, fanPulleyPosition]);

  const guardHeight = useMemo(() => {
    return Math.max(motorPulleyRadius, fanPulleyRadius) * 2 * 1.25;
  }, [motorPulleyRadius, fanPulleyRadius, motorPulleyPosition, fanPulleyPosition]);

  const guardPosition = useMemo(() => {
    const startPoint1 = [fanPulleyPosition[0], fanPulleyPosition[1] + fanPulleyRadius - beltThickness, fanPulleyPosition[2]];
    const endPoint1 = [motorPulleyPosition[0], motorPulleyPosition[1] + motorPulleyRadius - beltThickness, motorPulleyPosition[2]];

    const startPoint2 = [fanPulleyPosition[0], fanPulleyPosition[1] - fanPulleyRadius + beltThickness, fanPulleyPosition[2]];
    const endPoint2 = [motorPulleyPosition[0], motorPulleyPosition[1] - motorPulleyRadius + beltThickness, motorPulleyPosition[2]];

    return [
      (startPoint1[0] + endPoint1[0] + startPoint2[0] + endPoint2[0]) / 4,
      (startPoint1[1] + endPoint1[1] + startPoint2[1] + endPoint2[1]) / 4,
      motorPulleyPosition[2] - fromMM(30),
    ];
  }, [motorPulleyRadius, fanPulleyRadius, motorPulleyPosition, fanPulleyPosition]);

  const guardRotation = useMemo(() => {
    // Angle (radians) of the line from fan pulley center to motor pulley center.
    // atan2 handles all quadrants and the vertical case safely.
    const dx = motorPulleyPosition[0] - fanPulleyPosition[0];
    const dy = motorPulleyPosition[1] - fanPulleyPosition[1];
    return Math.atan2(dy, dx);
  }, [motorPulleyPosition, fanPulleyPosition]);

  return (
    <group>
      <mesh position={fanPulleyPosition} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[fanPulleyRadius, fanPulleyRadius, pulleyThickness, 32]} />
        <meshStandardMaterial color={"DarkGrey"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="Grey" widthTest={true} />
        </Edges>
      </mesh>

      {shaftPosition && (
        <mesh position={shaftPosition} rotation={[Math.PI / 2, 0, 0]} ref={shaftRef}>
          <cylinderGeometry args={[shaftRadius, shaftRadius, shaftLength, 6]} />
          <meshStandardMaterial color={"DarkGrey"} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>
      )}

      <mesh position={motorPulleyPosition} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[motorPulleyRadius, motorPulleyRadius, pulleyThickness, 32]} />
        <meshStandardMaterial color={"DarkGrey"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="Grey" widthTest={true} />
        </Edges>
      </mesh>

      <mesh geometry={beltGeometry}>
        <meshStandardMaterial color={"Black"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="Grey" widthTest={true} />
        </Edges>
      </mesh>

      <mesh geometry={beltGeometryBottom}>
        <meshStandardMaterial color={"Black"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="Grey" widthTest={true} />
        </Edges>
      </mesh>

      {hasGuard && <FanFrame width={guardWidth} height={guardHeight} depth={fromMM(80)} position={guardPosition} rotation={[0, 0, guardRotation]} />}
    </group>
  );
}

export function Motor({ position, rotation, radius, length, shaftLength, shaftSide }) {
  return (
    <group position={position}>
      <mesh rotation={rotation}>
        <cylinderGeometry args={[radius, radius, length, 32]} />
        <meshStandardMaterial color={"DarkGrey"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="Grey" widthTest={true} />
        </Edges>
      </mesh>
      <mesh position={[0, 0, (length / 2 + shaftLength / 2) * shaftSide]} rotation={rotation}>
        <cylinderGeometry args={[0.01, 0.01, shaftLength, 32]} />
        <meshStandardMaterial color={"Black"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="Grey" widthTest={true} />
        </Edges>
      </mesh>
    </group>
  );
}

export function MotorBase({ position, length, width, height }) {
  return (
    <mesh position={position}>
      <boxGeometry args={[length, height, width]} />
      <meshStandardMaterial color={"DarkGrey"} />
      <Edges scale={1} renderOrder={1000}>
        <meshBasicMaterial transparent color="Grey" widthTest={true} />
      </Edges>
    </mesh>
  );
}

export function ShaftBase({ position, length, width, height }) {
  return (
    <mesh position={position}>
      <boxGeometry args={[length, height, width]} />
      <meshStandardMaterial color={"DarkGrey"} />
      <Edges scale={1} renderOrder={1000}>
        <meshBasicMaterial transparent color="Grey" widthTest={true} />
      </Edges>
    </mesh>
  );
}

export function MountingFoot({ radius, height, position }) {
  return (
    <mesh position={position}>
      <cylinderGeometry args={[radius, radius, height, 32]} />
      <meshStandardMaterial color={"Black"} />
      <Edges scale={1} renderOrder={1000}>
        <meshBasicMaterial transparent color="Grey" widthTest={true} />
      </Edges>
    </mesh>
  );
}

export function MountingFrame({ width, height, length, position = [0, 0, 0], rotation = [0, 0, 0] }) {
  return (
    <mesh position={position} rotation={rotation}>
      <boxGeometry args={[length, height, width]} />
      <meshStandardMaterial color={"DarkGrey"} />
      <Edges scale={1} renderOrder={1000}>
        <meshBasicMaterial transparent color="Grey" widthTest={true} />
      </Edges>
    </mesh>
  );
}

export function FanFrame({ width, height, depth, position = [0, 0, 0], rotation = [0, 0, 0] }) {
  var extrudeSettings = {
    depth: depth,
    steps: 1,
    bevelEnabled: false,
    curveSegments: 16,
  };

  const frameWidth = fromMM(30);

  const geom = useMemo(() => {
    return buildDuctPlateWithHole(height, width, false, width - frameWidth * 2, height - frameWidth * 2, 0, frameWidth, fromMM(2));
  }, [height, width]);

  const lip_geometry = useMemo(() => {
    return buildDuct(height, width, false, width, height, 0, 0, fromMM(2));
  }, [height, width]);

  return (
    <group position={position} rotation={rotation}>
      <mesh geometry={geom}>
        <meshStandardMaterial color={"DarkGrey"} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="Grey" widthTest={true} />
        </Edges>
      </mesh>
      <mesh>
        <extrudeGeometry args={[lip_geometry, extrudeSettings]} />
        <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="Grey" widthTest={true} />
        </Edges>
      </mesh>
    </group>
  );
}

useGLTF.preload("/fan.glb");
